/**
 * Adder Mapping tab — Story SCH-2.3.
 *
 * Replaces AdderMappingTabPlaceholder.tsx in the 5-tab Configure panel.
 *
 * Lets a Tenant Admin map distinct SubContractorHub adder names (discovered
 * from a sample of recent SCH projects) to entries in the Joules adder
 * catalog. Per discovered name the admin can:
 *   - Confirm an auto-match suggestion (Jaccard similarity over name).
 *   - Pick a different existing catalog entry.
 *   - Create a new catalog entry inline (atomic insert + map).
 *   - Skip the adder (Epic 3 will omit it from project snapshots).
 *
 * Architecture refs:
 *   - FR21–FR25 (discovery + auto-match + inline catalog creation +
 *     translate_adders semantics)
 *   - D5 (mapping table pattern)
 *   - AR21 (tenant scoping at app layer; backend enforces; FE relies on it)
 *   - AC1–AC13 in docs/stories/SCH-2.3-adder-mapping-tab.md
 *
 * Architectural note: there is NO drag-precedence for adders — adder
 * mapping is N→1 catalog lookup, not ordered rules. The visual layout
 * has three sections: Discovered (un-mapped) / Confirmed / Skipped.
 */

import { useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  Loader2,
  Pencil,
  Plus,
  RotateCcw,
  Sparkles,
  Trash2,
  Undo2,
  X,
} from 'lucide-react';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { SearchableSelect } from '@/components/ui/searchable-select';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/components/ui/use-toast';

import {
  useCreateAdderMapping,
  useCreateAdderWithMapping,
  useDeleteAdderMapping,
  useDiscoverAdders,
  useUpdateAdderMapping,
} from '@/hooks/integrations/useAdderMappings';
import { useQueryClient } from '@tanstack/react-query';
import { useTenant } from '@/contexts/TenantContext';
import { discoverAddersQueryKey } from '@/hooks/integrations/useAdderMappings';
import type {
  AdderCandidate,
  AdderMappingRow,
  AdderPricingModel,
  AutoMatchSuggestion,
  NewAdderInput,
} from '@/types/integrations/adderMapping';

const HIGH_CONFIDENCE_MIN = 0.85;

interface AdderMappingTabProps {
  /**
   * Reports mutation pending state to the parent panel so the panel can
   * block onClose while any mutation is in flight (AC10).
   */
  onMutationPendingChange?: (pending: boolean) => void;
}

// ───────────────────────────────────────────────────────────────────────────
//   Main tab component
// ───────────────────────────────────────────────────────────────────────────
export function AdderMappingTab({ onMutationPendingChange }: AdderMappingTabProps) {
  const { toast } = useToast();
  const { currentTenant } = useTenant();
  const queryClient = useQueryClient();
  const discoverQuery = useDiscoverAdders();

  const createMutation = useCreateAdderMapping();
  const createWithMappingMutation = useCreateAdderWithMapping();
  const updateMutation = useUpdateAdderMapping();
  const deleteMutation = useDeleteAdderMapping();

  const isAnyMutationPending =
    createMutation.isPending ||
    createWithMappingMutation.isPending ||
    updateMutation.isPending ||
    deleteMutation.isPending;

  useEffect(() => {
    onMutationPendingChange?.(isAnyMutationPending);
  }, [isAnyMutationPending, onMutationPendingChange]);

  // Track which discovered row currently has its inline create-form open.
  // Only one form can be open at a time to keep the UX focused.
  const [inlineCreateForName, setInlineCreateForName] = useState<string | null>(null);
  // Track which confirmed row is in inline-edit mode.
  const [editingMappingId, setEditingMappingId] = useState<string | null>(null);

  // ─── Loading + error states (mirror SCH-2.1 / 2.2 patterns) ─────────────
  if (discoverQuery.isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-5 w-5 animate-spin text-primary" />
      </div>
    );
  }

  if (discoverQuery.isError) {
    const err = discoverQuery.error as Error & { code?: string };
    return (
      <Alert variant="destructive">
        <AlertTriangle className="h-4 w-4" />
        <AlertTitle>Could not load SubContractorHub adders</AlertTitle>
        <AlertDescription>
          {err.code === 'auth_failed'
            ? 'Connection authentication failed. Rotate the API key on the Connection tab and try again.'
            : err.message || 'Try refreshing the panel.'}
        </AlertDescription>
      </Alert>
    );
  }

  const data = discoverQuery.data;
  if (!data) return null;

  const { schAdderNames, joulesAdders, suggestions, existingMappings } = data;

  // Index existing mappings by lowercased name for fast lookup.
  const mappingByLowerName = new Map<string, AdderMappingRow>();
  for (const m of existingMappings) {
    mappingByLowerName.set(m.externalAdderName.trim().toLowerCase(), m);
  }

  // Partition rows into three sections:
  //   - Discovered & un-mapped: SCH name with no existing mapping.
  //   - Confirmed: existing mapping with joules_adder_id != null AND enabled.
  //   - Skipped: existing mapping with joules_adder_id == null OR enabled === false.
  const undiscovered = schAdderNames.filter(
    (name) => !mappingByLowerName.has(name.trim().toLowerCase()),
  );
  const confirmed = existingMappings.filter(
    (m) => m.joulesAdderId !== null && m.enabled,
  );
  const skipped = existingMappings.filter(
    (m) => m.joulesAdderId === null || !m.enabled,
  );

  // ─── Mutation handlers ──────────────────────────────────────────────────
  function handleConfirm(name: string, joulesAdderId: number) {
    createMutation.mutate(
      { externalAdderName: name, joulesAdderId, enabled: true },
      {
        onSuccess: (result) => {
          if (result.error) {
            toast({
              title: 'Could not save mapping',
              description: result.error.message,
              variant: 'destructive',
            });
          } else {
            toast({ title: `Mapped ${name}` });
          }
        },
        onError: () =>
          toast({
            title: 'Could not save mapping',
            description: 'Please try again.',
            variant: 'destructive',
          }),
      },
    );
  }

  function handleSkip(name: string) {
    createMutation.mutate(
      { externalAdderName: name, joulesAdderId: null, enabled: false },
      {
        onSuccess: (result) => {
          if (result.error) {
            toast({
              title: 'Could not skip adder',
              description: result.error.message,
              variant: 'destructive',
            });
          } else {
            toast({ title: `Skipped ${name}` });
          }
        },
      },
    );
  }

  function handleCreateInline(name: string, adder: NewAdderInput) {
    createWithMappingMutation.mutate(
      { externalAdderName: name, adder },
      {
        onSuccess: (result) => {
          if (result.error) {
            toast({
              title: 'Could not create catalog entry',
              description: result.error.message,
              variant: 'destructive',
            });
          } else {
            toast({ title: `Created ${adder.name} and mapped ${name}` });
            setInlineCreateForName(null);
          }
        },
      },
    );
  }

  function handleUpdate(id: string, joulesAdderId: number) {
    updateMutation.mutate(
      { id, body: { joulesAdderId, enabled: true } },
      {
        onSuccess: (result) => {
          if (result.error) {
            toast({
              title: 'Could not update mapping',
              description: result.error.message,
              variant: 'destructive',
            });
          } else {
            toast({ title: 'Mapping updated' });
            setEditingMappingId(null);
          }
        },
      },
    );
  }

  function handleUndoSkip(mapping: AdderMappingRow) {
    // Undo skip = delete the skip row, returning the SCH name to "discovered".
    deleteMutation.mutate(mapping.id, {
      onSuccess: (result) => {
        if (result.error) {
          toast({
            title: 'Could not undo skip',
            description: result.error.message,
            variant: 'destructive',
          });
        } else {
          toast({ title: `Undid skip: ${mapping.externalAdderName}` });
        }
      },
    });
  }

  function handleDelete(mapping: AdderMappingRow) {
    deleteMutation.mutate(mapping.id, {
      onSuccess: (result) => {
        if (result.error) {
          toast({
            title: 'Could not remove mapping',
            description: result.error.message,
            variant: 'destructive',
          });
        } else {
          toast({ title: `Removed mapping: ${mapping.externalAdderName}` });
        }
      },
    });
  }

  function handleRediscover() {
    // Forces a refetch of the discover-adders query — picks up any new SCH
    // adder names added to projects since the last refresh.
    queryClient.invalidateQueries({
      queryKey: discoverAddersQueryKey(currentTenant?.tenant_id),
    });
  }

  // Per-row pending helpers — scoped via mutation.variables to avoid the
  // SCH-2.1 follow-up regression where global mutation.isPending froze
  // every row.
  const confirmingName =
    createMutation.isPending ? createMutation.variables?.externalAdderName : undefined;
  const inlineCreatingName =
    createWithMappingMutation.isPending
      ? createWithMappingMutation.variables?.externalAdderName
      : undefined;
  const updatingId = updateMutation.isPending ? updateMutation.variables?.id : undefined;
  const deletingId = deleteMutation.isPending ? deleteMutation.variables : undefined;

  // ─── Render ─────────────────────────────────────────────────────────────
  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold">Adder Mapping</h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            Map adder names found in SubContractorHub projects to entries in
            your Joules adder catalog. Confirm a suggested match, pick a
            different one, create a new catalog entry inline, or skip an
            adder you don&apos;t want imported.
          </p>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={handleRediscover}
          disabled={isAnyMutationPending}
        >
          <RotateCcw className="mr-1 h-3.5 w-3.5" />
          Re-discover
        </Button>
      </div>

      {schAdderNames.length === 0 && existingMappings.length === 0 && (
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>No adders discovered</AlertTitle>
          <AlertDescription>
            The recent SubContractorHub project sample contained no adder line
            items. Add an adder to a SubContractorHub project, then click
            Re-discover.
          </AlertDescription>
        </Alert>
      )}

      {/* ── Discovered & un-mapped section ─────────────────────────────── */}
      {undiscovered.length > 0 && (
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <CardTitle className="text-base">Discovered adders</CardTitle>
              <Badge variant="outline">{undiscovered.length}</Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-2">
            {undiscovered.map((name) => {
              const suggestion = suggestions[name.trim().toLowerCase()];
              const isInlineOpen = inlineCreateForName === name;
              const rowIsPending =
                confirmingName === name || inlineCreatingName === name;
              return (
                <div key={name}>
                  <DiscoveredAdderRow
                    name={name}
                    suggestion={suggestion}
                    candidates={joulesAdders}
                    isPending={rowIsPending}
                    isAnyMutationPending={isAnyMutationPending}
                    isInlineCreateOpen={isInlineOpen}
                    onConfirm={(adderId) => handleConfirm(name, adderId)}
                    onSkip={() => handleSkip(name)}
                    onOpenInlineCreate={() => setInlineCreateForName(name)}
                    onCloseInlineCreate={() => setInlineCreateForName(null)}
                  />
                  {isInlineOpen && (
                    <InlineCreateAdderForm
                      schAdderName={name}
                      isPending={inlineCreatingName === name}
                      onSave={(adder) => handleCreateInline(name, adder)}
                      onCancel={() => setInlineCreateForName(null)}
                    />
                  )}
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      {/* ── Confirmed mappings section ────────────────────────────────── */}
      {confirmed.length > 0 && (
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <CardTitle className="text-base">Confirmed mappings</CardTitle>
              <Badge variant="outline">{confirmed.length}</Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-2">
            {confirmed.map((mapping) => (
              <ConfirmedAdderRow
                key={mapping.id}
                mapping={mapping}
                candidates={joulesAdders}
                isEditing={editingMappingId === mapping.id}
                isPending={updatingId === mapping.id || deletingId === mapping.id}
                isAnyMutationPending={isAnyMutationPending}
                onStartEdit={() => setEditingMappingId(mapping.id)}
                onCancelEdit={() => setEditingMappingId(null)}
                onSave={(adderId) => handleUpdate(mapping.id, adderId)}
                onDelete={() => handleDelete(mapping)}
              />
            ))}
          </CardContent>
        </Card>
      )}

      {/* ── Skipped section ───────────────────────────────────────────── */}
      {skipped.length > 0 && (
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <CardTitle className="text-base">Skipped</CardTitle>
              <Badge variant="outline">{skipped.length}</Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-2">
            {skipped.map((mapping) => (
              <SkippedAdderRow
                key={mapping.id}
                mapping={mapping}
                isPending={deletingId === mapping.id}
                isAnyMutationPending={isAnyMutationPending}
                onUndo={() => handleUndoSkip(mapping)}
              />
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
//   Discovered adder row — un-mapped state with auto-match + actions
// ───────────────────────────────────────────────────────────────────────────
interface DiscoveredAdderRowProps {
  name: string;
  suggestion: AutoMatchSuggestion | null | undefined;
  candidates: AdderCandidate[];
  isPending: boolean;
  isAnyMutationPending: boolean;
  isInlineCreateOpen: boolean;
  onConfirm: (adderId: number) => void;
  onSkip: () => void;
  onOpenInlineCreate: () => void;
  onCloseInlineCreate: () => void;
}

function DiscoveredAdderRow({
  name,
  suggestion,
  candidates,
  isPending,
  isAnyMutationPending,
  isInlineCreateOpen,
  onConfirm,
  onSkip,
  onOpenInlineCreate,
  onCloseInlineCreate,
}: DiscoveredAdderRowProps) {
  // Pre-select the auto-match suggestion (if any) in the dropdown so a
  // single Confirm click maps the row to its highest-confidence guess.
  const [selectedAdderId, setSelectedAdderId] = useState<number | null>(
    suggestion?.joulesTargetId ?? null,
  );

  const isHighConfidence = suggestion && suggestion.confidence >= HIGH_CONFIDENCE_MIN;
  const candidate = candidates.find((c) => c.id === selectedAdderId);

  // Stacked layout: full SCH adder name above the action row so the
  // name has the entire panel width to breathe (long names like
  // "Main Service Panel Upgrade — 200A" used to truncate behind the
  // dropdown + button cluster).
  return (
    <div className="rounded-md border bg-card p-2 space-y-2">
      <div className="flex items-start gap-2 flex-wrap">
        <span className="text-sm font-medium break-words">{name}</span>
        {isHighConfidence && (
          <Badge variant="secondary" className="text-xs">
            <Sparkles className="mr-1 h-3 w-3" />
            Auto-match
          </Badge>
        )}
        {suggestion && (
          <span className="text-xs text-muted-foreground basis-full">
            Suggested:{' '}
            <strong>
              {candidates.find((c) => c.id === suggestion.joulesTargetId)?.name ?? 'unknown'}
            </strong>
            {' '}({Math.round(suggestion.confidence * 100)}% confidence)
          </span>
        )}
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <AdderCandidateSelector
          candidates={candidates}
          value={selectedAdderId}
          onChange={setSelectedAdderId}
          disabled={isPending || isAnyMutationPending}
        />

        <Button
          size="sm"
          variant="default"
          disabled={!candidate || isPending || isAnyMutationPending}
          onClick={() => candidate && onConfirm(candidate.id)}
        >
          {isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Confirm'}
        </Button>

        <Button
          size="sm"
          variant="outline"
          disabled={isPending || isAnyMutationPending}
          onClick={isInlineCreateOpen ? onCloseInlineCreate : onOpenInlineCreate}
        >
          <Plus className="mr-1 h-3.5 w-3.5" />
          Create new
        </Button>

        <Button
          size="sm"
          variant="ghost"
          disabled={isPending || isAnyMutationPending}
          onClick={onSkip}
        >
          Skip
        </Button>
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
//   Confirmed adder row — locked-in mapping with edit/delete affordances
// ───────────────────────────────────────────────────────────────────────────
interface ConfirmedAdderRowProps {
  mapping: AdderMappingRow;
  candidates: AdderCandidate[];
  isEditing: boolean;
  isPending: boolean;
  isAnyMutationPending: boolean;
  onStartEdit: () => void;
  onCancelEdit: () => void;
  onSave: (adderId: number) => void;
  onDelete: () => void;
}

function ConfirmedAdderRow({
  mapping,
  candidates,
  isEditing,
  isPending,
  isAnyMutationPending,
  onStartEdit,
  onCancelEdit,
  onSave,
  onDelete,
}: ConfirmedAdderRowProps) {
  const [draftAdderId, setDraftAdderId] = useState<number | null>(mapping.joulesAdderId);

  // Reset draft when caller exits edit mode (e.g., after a successful save
  // or cancel — keeps the dropdown showing the latest persisted target).
  useEffect(() => {
    if (!isEditing) setDraftAdderId(mapping.joulesAdderId);
  }, [isEditing, mapping.joulesAdderId]);

  const targetName =
    mapping.joulesAdderName ??
    candidates.find((c) => c.id === mapping.joulesAdderId)?.name ??
    'Adder no longer exists';
  const isDormant = mapping.joulesAdderId !== null && mapping.joulesAdderName === null;

  // Single-row layout: [icon] [SCH name] → [Joules name OR dropdown] [actions].
  // In edit mode the dropdown replaces the assigned-adder name in-place so
  // the row's structure doesn't shift visually when toggling edit on/off.
  return (
    <div className="flex items-center gap-2 rounded-md border bg-card p-2">
      <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600" />
      <span className="text-sm font-medium break-words">{mapping.externalAdderName}</span>
      <span className="text-xs text-muted-foreground">→</span>

      {isEditing ? (
        <AdderCandidateSelector
          candidates={candidates}
          value={draftAdderId}
          onChange={setDraftAdderId}
          disabled={isPending || isAnyMutationPending}
        />
      ) : (
        <>
          <span
            className={`text-sm break-words ${isDormant ? 'text-destructive italic' : ''}`}
          >
            {targetName}
          </span>
          {isDormant && (
            <Badge variant="destructive" className="text-xs">
              Dormant
            </Badge>
          )}
        </>
      )}

      <div className="ml-auto flex items-center gap-1 shrink-0">
        {isEditing ? (
          <>
            <Button
              size="sm"
              variant="default"
              disabled={
                !draftAdderId ||
                draftAdderId === mapping.joulesAdderId ||
                isPending ||
                isAnyMutationPending
              }
              onClick={() => draftAdderId && onSave(draftAdderId)}
            >
              {isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Save'}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              disabled={isPending || isAnyMutationPending}
              onClick={onCancelEdit}
            >
              Cancel
            </Button>
          </>
        ) : (
          <>
            <Button
              size="sm"
              variant="ghost"
              aria-label="Edit mapping"
              disabled={isPending || isAnyMutationPending}
              onClick={onStartEdit}
            >
              <Pencil className="h-3.5 w-3.5" />
            </Button>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  size="sm"
                  variant="ghost"
                  aria-label="Delete mapping"
                  disabled={isPending || isAnyMutationPending}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-56" align="end">
                <p className="text-sm mb-2">Remove this mapping?</p>
                <div className="flex gap-2 justify-end">
                  <Button size="sm" variant="destructive" onClick={onDelete}>
                    {isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Remove'}
                  </Button>
                </div>
              </PopoverContent>
            </Popover>
          </>
        )}
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
//   Skipped row — simple "skipped" badge + Undo
// ───────────────────────────────────────────────────────────────────────────
interface SkippedAdderRowProps {
  mapping: AdderMappingRow;
  isPending: boolean;
  isAnyMutationPending: boolean;
  onUndo: () => void;
}

function SkippedAdderRow({
  mapping,
  isPending,
  isAnyMutationPending,
  onUndo,
}: SkippedAdderRowProps) {
  return (
    <div className="flex items-center gap-2 rounded-md border bg-muted/30 p-2">
      <X className="h-4 w-4 shrink-0 text-muted-foreground" />
      <div className="flex-1 min-w-0">
        <span className="text-sm truncate">{mapping.externalAdderName}</span>
        <span className="text-xs text-muted-foreground ml-2">(skipped)</span>
      </div>
      <Button
        size="sm"
        variant="ghost"
        disabled={isPending || isAnyMutationPending}
        onClick={onUndo}
      >
        <Undo2 className="mr-1 h-3.5 w-3.5" />
        {isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Undo'}
      </Button>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
//   Inline create-adder form
// ───────────────────────────────────────────────────────────────────────────
interface InlineCreateAdderFormProps {
  schAdderName: string;
  isPending: boolean;
  onSave: (adder: NewAdderInput) => void;
  onCancel: () => void;
}

function InlineCreateAdderForm({
  schAdderName,
  isPending,
  onSave,
  onCancel,
}: InlineCreateAdderFormProps) {
  // Pre-fill the catalog name with the SCH adder name — admins can edit
  // before saving if they want a cleaner display name in the catalog.
  const [name, setName] = useState(schAdderName);
  const [category, setCategory] = useState('');
  const [pricingModel, setPricingModel] = useState<AdderPricingModel>('quote');
  const [unitPrice, setUnitPrice] = useState<string>('');
  const [unitLabel, setUnitLabel] = useState('');

  // Validation rules mirror addersService.createAdder:
  //   - name required.
  //   - unit_price required (numeric, >= 0) when pricing_model != 'quote'.
  //   - unit_label only meaningful for 'per_unit'.
  const trimmedName = name.trim();
  const requiresUnitPrice = pricingModel !== 'quote';
  const parsedUnitPrice = requiresUnitPrice ? Number(unitPrice) : null;
  const unitPriceValid =
    !requiresUnitPrice ||
    (unitPrice !== '' && Number.isFinite(parsedUnitPrice) && (parsedUnitPrice ?? 0) >= 0);
  const isFormValid = trimmedName.length > 0 && unitPriceValid;

  function handleSubmit() {
    onSave({
      name: trimmedName,
      category: category.trim() || null,
      pricingModel,
      unitPrice: requiresUnitPrice ? parsedUnitPrice : null,
      unitLabel: pricingModel === 'per_unit' ? unitLabel.trim() || null : null,
      isActive: true,
    });
  }

  return (
    <div className="ml-4 mt-2 mb-2 rounded-md border bg-muted/20 p-3 space-y-3">
      <p className="text-xs text-muted-foreground">
        Create a new catalog entry for <strong>{schAdderName}</strong>. The
        new entry will be added to your Joules adder catalog and linked to
        this SubContractorHub adder name in one step.
      </p>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label htmlFor="inline-adder-name" className="text-xs">
            Catalog name
          </Label>
          <Input
            id="inline-adder-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={isPending}
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="inline-adder-category" className="text-xs">
            Category (optional)
          </Label>
          <Input
            id="inline-adder-category"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            disabled={isPending}
            placeholder="e.g., Electrical"
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="inline-adder-pricing" className="text-xs">
            Pricing model
          </Label>
          <Select
            value={pricingModel}
            onValueChange={(v) => setPricingModel(v as AdderPricingModel)}
            disabled={isPending}
          >
            <SelectTrigger id="inline-adder-pricing">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="quote">Quote (price varies)</SelectItem>
              <SelectItem value="fixed">Fixed price</SelectItem>
              <SelectItem value="per_unit">Per unit</SelectItem>
              <SelectItem value="per_watt">Per watt</SelectItem>
              <SelectItem value="per_foot">Per foot</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {requiresUnitPrice && (
          <div className="space-y-1">
            <Label htmlFor="inline-adder-price" className="text-xs">
              Unit price ($)
            </Label>
            <Input
              id="inline-adder-price"
              type="number"
              step="0.01"
              min="0"
              value={unitPrice}
              onChange={(e) => setUnitPrice(e.target.value)}
              disabled={isPending}
            />
          </div>
        )}
        {pricingModel === 'per_unit' && (
          <div className="space-y-1 col-span-2">
            <Label htmlFor="inline-adder-unit-label" className="text-xs">
              Unit label (e.g., panel, outlet)
            </Label>
            <Input
              id="inline-adder-unit-label"
              value={unitLabel}
              onChange={(e) => setUnitLabel(e.target.value)}
              disabled={isPending}
            />
          </div>
        )}
      </div>

      <div className="flex justify-end gap-2">
        <Button size="sm" variant="ghost" disabled={isPending} onClick={onCancel}>
          Cancel
        </Button>
        <Button size="sm" disabled={!isFormValid || isPending} onClick={handleSubmit}>
          {isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Save catalog entry'}
        </Button>
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
//   Searchable dropdown for picking from the existing adder catalog
// ───────────────────────────────────────────────────────────────────────────
interface AdderCandidateSelectorProps {
  candidates: AdderCandidate[];
  value: number | null;
  onChange: (adderId: number | null) => void;
  disabled?: boolean;
}

function AdderCandidateSelector({
  candidates,
  value,
  onChange,
  disabled,
}: AdderCandidateSelectorProps) {
  // Surface category as the description line under each option (the
  // SearchableSelect primitive doesn't support group headers — see
  // searchable-select.tsx). Sort by category then name to keep the
  // list scannable for tenants with large catalogs.
  const options = useMemo(
    () =>
      candidates
        .map((c) => ({
          value: String(c.id),
          label: c.name,
          description: c.category ?? undefined,
        }))
        .sort((a, b) => {
          const aCat = a.description ?? '￿';
          const bCat = b.description ?? '￿';
          if (aCat !== bCat) return aCat.localeCompare(bCat);
          return a.label.localeCompare(b.label);
        }),
    [candidates],
  );

  return (
    <SearchableSelect
      options={options}
      value={value === null ? '' : String(value)}
      onValueChange={(v) => onChange(v ? Number(v) : null)}
      placeholder="Pick an adder…"
      disabled={disabled}
      className="min-w-[12rem] max-w-[16rem]"
    />
  );
}
